import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Fade,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import CodeIcon from "@mui/icons-material/Code";
import WorkIcon from "@mui/icons-material/Work";
import EmailIcon from "@mui/icons-material/Email";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { trackEvent } from '../../services/analytics';

interface NavigationProps {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ isDarkMode, toggleTheme }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { text: "Home", path: "/", icon: <HomeIcon /> },
    { text: "Über uns", path: "/about", icon: <InfoIcon /> },
    { text: "Services", path: "/services", icon: <CodeIcon /> },
    { text: "Portfolio", path: "/portfolio", icon: <WorkIcon /> },
    { text: "Kontakt", path: "/contact", icon: <EmailIcon /> },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (itemName: string) => {
    trackEvent('Navigation', 'Menüklick', itemName);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" component="div">
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle} edge="end" aria-label="Menü schließen">
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ pt: 2 }}>
        {navigationItems.map((item) => (
          <ListItem
            key={item.text}
            component={RouterLink}
            to={item.path}
            onClick={() => handleNavClick(item.text)}
            aria-label={`Zur Seite ${item.text} navigieren`}
            sx={{
              color: "text.primary",
              py: 2,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              ...(location.pathname === item.path && {
                color: "primary.main",
                backgroundColor: "action.selected",
              }),
            }}
          >
            <ListItemIcon
              sx={{
                color:
                  location.pathname === item.path ? "primary.main" : "inherit",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 600 : 400,
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "background.default",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: "blur(8px)",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 72 }}>
            <Box
              component={RouterLink}
              to="/"
              aria-label="Zur Startseite navigieren"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                mr: 4,
              }}
            >
              <Box
                component="img"
                src={isDarkMode ? "/logo-dark.svg" : "/logo-light.svg"}
                alt="pyzon"
                sx={{ height: 70 }}
              />
            </Box>

            {isMobile ? (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={toggleTheme}
                  aria-label={isDarkMode ? "Zum hellen Modus wechseln" : "Zum dunklen Modus wechseln"}
                  sx={{
                    mr: 1,
                    color: "text.primary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                <IconButton
                  edge="start"
                  aria-label="Menü öffnen"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: "text.primary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.path}
                    aria-label={`Zur Seite ${item.text} navigieren`}
                    onClick={() => handleNavClick(item.text)}
                    sx={{
                      mx: 1,
                      color: "text.primary",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: 2,
                        backgroundColor: "primary.main",
                        transform:
                          location.pathname === item.path
                            ? "scaleX(1)"
                            : "scaleX(0)",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
                <IconButton
                  onClick={toggleTheme}
                  aria-label={isDarkMode ? "Zum hellen Modus wechseln" : "Zum dunklen Modus wechseln"}
                  sx={{
                    ml: 2,
                    color: "text.primary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Toolbar /> {/* Spacer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navigation;
